import { getEffectiveToday, isOpenEverything } from '../../../utils/systemTime';

// ALLOW_PDF_IMAGES_V1
import { resolveAssignmentInstanceLabel } from '../../../utils/assignmentLabels';
// TRADITIONAL_GRADING_PATCH_20260813
import { useState, useEffect, useMemo } from 'react';
import { useStudentDataRefreshTick } from '../../../utils/studentDataRefresh';
import RichTextEditor from '../../../components/RichTextEditor';
import StudentInstructorFeedback from './StudentInstructorFeedback';
import StudentPeerReview from './StudentPeerReview';
import StudentStructuredForm from './StudentStructuredForm';
import ReceivedPeerReviews from './ReceivedPeerReviews';
import {
  isInlineReviewableFile
} from '../../../utils/reviewablePdf';
import StudentScheduledAssignments from './StudentScheduledAssignments';
import ForwardedAssignmentFeedback from './ForwardedAssignmentFeedback';
import AssignmentGroupMembers from './AssignmentGroupMembers';
import AssignmentInstructions from './AssignmentInstructions';
import MultiFileSubmissionControls from './MultiFileSubmissionControls';
import {
  fetchSubmissionFileSlots,
  getSubmissionFileAccept,
  getSubmissionFileExtension,
  getSubmissionFileFormatLabel,
  normalizeSubmissionFileSettings,
  safeSubmissionFilePart,
  validateConfiguredSubmissionFile
} from '../../../utils/assignmentSubmissionFiles';
import {
  applyStudentAssignmentTiming,
  getStudentAssignmentBaseDueDate,
  getStudentAssignmentDueDate,
  getStudentAssignmentOpenDate
} from '../../../utils/assignmentTiming';

import {
  loadStudentStructuredFormCheckpoints
} from '../../../utils/structuredFormStudentTiming';

// STRUCTURED_FORM_NEXT_INSTANCE_DUE_V1

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(name,requires_submission,instructions,assignment_kind)&order=instance_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchStructuredFormInstances(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instances?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}&order=instance_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchStudentAssignmentGroupMemberships(enrolmentId) {
  if (!enrolmentId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${enrolmentId}&select=course_assignment_id,enrolment_id,group_number`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  return response.json();
}

async function fetchAssignmentGroupRoster(courseId) {
  if (!courseId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=course_assignment_id,enrolment_id,group_number,enrolments(id,students(student_code,english_name,chinese_name,is_master)),course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to load assignment group rosters (${response.status})`
    );
  }

  return response.json();
}

async function fetchStudentAssignmentGroupDeadlines(courseId) {
  if (!courseId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?select=course_assignment_id,group_number,open_date,due_date,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );

  return response.json();
}

async function fetchTypeDescriptions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}


async function fetchAiDisclosureContext(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [settingsResponse, permissionsResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=*`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_ai_permissions?course_id=eq.${courseId}&permitted=eq.true&select=id,note,ai_use_types(label,description,ai_use_categories(name,display_order))`,
      { headers }
    )
  ]);

  if (!settingsResponse.ok) {
    throw new Error(
      `Failed to load AI Disclosure settings (${settingsResponse.status})`
    );
  }

  if (!permissionsResponse.ok) {
    throw new Error(
      `Failed to load permitted AI uses (${permissionsResponse.status})`
    );
  }

  const settings = await settingsResponse.json();
  const permissions = await permissionsResponse.json();

  const settingsMap = {};
  (Array.isArray(settings) ? settings : []).forEach(row => {
    settingsMap[row.assignment_type_id] =
      !!row.ai_disclosure_enabled;
  });

  const permittedAiUses = (Array.isArray(permissions) ? permissions : [])
    .filter(row => row.permitted !== false)
    .sort((a, b) => {
      const aOrder =
        Number(a.ai_use_types?.ai_use_categories?.display_order) || 0;
      const bOrder =
        Number(b.ai_use_types?.ai_use_categories?.display_order) || 0;

      if (aOrder !== bOrder) return aOrder - bOrder;

      return String(a.ai_use_types?.label || '').localeCompare(
        String(b.ai_use_types?.label || '')
      );
    });

  return {
    settingsMap,
    permittedAiUses,
    assignmentTypeSettings:
      Array.isArray(settings) ? settings : []
  };
}

async function fetchSubmissions(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${enrolmentId}&select=*,assignment_feedback(*)`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function createSubmission(enrolmentId, courseAssignmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        course_assignment_id: courseAssignmentId,
        submitted: false
      })
    }
  );
  return response.json();
}

async function updateSubmission(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function submitFeedbackResponse(submissionId, responseHtml) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_feedback?assignment_submission_id=eq.${submissionId}&submitted=eq.true`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        student_response: responseHtml
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to submit response (${response.status})`);
  }

  const result = await response.json();

  if (!result?.[0]) {
    throw new Error('Instructor feedback record was not found.');
  }

  return result[0];
}

function hasMeaningfulRichText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length > 0;
}

function getAssignmentStatus(assignment, session) {
  if (isOpenEverything(session)) return 'open';

  if (assignment?.student_assignment_applicable === false) {
    return 'not_applicable';
  }

  const today = getEffectiveToday(session);

  const requiresSubmission =
    assignment.assignment_types?.requires_submission ?? true;

  if (!requiresSubmission) return 'no_submission';

  if (
    assignment.assignment_types?.assignment_kind ===
    'structured_form'
  ) {
    const today =
      getEffectiveToday(session);

    const openDate =
      assignment.open_date
        ? String(assignment.open_date).slice(0, 10)
        : '';

    const dueDate =
      assignment.due_date
        ? String(assignment.due_date).slice(0, 10)
        : '';

    const closeDate =
      assignment.close_date
        ? String(assignment.close_date).slice(0, 10)
        : '';

    if (
      openDate &&
      today < openDate
    ) {
      return 'not_open';
    }

    if (
      closeDate &&
      today > closeDate
    ) {
      return 'closed';
    }

    if (
      dueDate &&
      today > dueDate
    ) {
      return 'past_due';
    }

    return 'open';
  }

  const openDate =
    getStudentAssignmentOpenDate(assignment);
  const dueDate =
    getStudentAssignmentDueDate(assignment);

  /*
   * A due date is required.
   *
   * An open date is optional: null means there is
   * no opening restriction.
   */
  if (!dueDate) {
    return 'not_configured';
  }

  if (
    openDate &&
    today < openDate
  ) {
    return 'not_open';
  }

  if (today <= dueDate) {
    return 'open';
  }

  return 'closed';
}

function formatDate(dateStr) {
  if (!dateStr) return '';

  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}


function isValidHttpsUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function AiPolicyInfo({ permittedAiUses }) {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        In this course, AI can be used in only <u>the following ways</u>:
      </div>

      {permittedAiUses.length === 0 ? (
        <div style={styles.noAiAllowed}>
          This course does not currently permit any uses of AI.
        </div>
      ) : (
        <div style={styles.permissionList}>
          {permittedAiUses.map(permission => (
            <div key={permission.id} style={styles.permissionItem}>
              <div style={styles.permissionTitle}>
                {permission.ai_use_types?.label || 'Permitted AI use'}
              </div>
              {permission.ai_use_types?.description && (
                <div style={styles.permissionDescription}>
                  {permission.ai_use_types.description}
                </div>
              )}
              {permission.note && (
                <div style={styles.permissionNote}>
                  Course note: {permission.note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SharingUrlInfo() {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        The Sharing URL is not the same as the URL of your conversation.
      </div>
      <div style={styles.infoText}>
        Use the conversation's Share control, create a sharing link, then
        paste that sharing URL here.
      </div>

      <div style={styles.providerGuideList}>
        {[
          ['ChatGPT', 'Open conversation', 'Share', 'Create / copy link'],
          ['Claude', 'Open conversation', 'Share', 'Create / copy link'],
          ['Gemini', 'Open conversation', 'Share', 'Create / copy public link']
        ].map(([provider, step1, step2, step3]) => (
          <div key={provider} style={styles.providerGuide}>
            <div style={styles.providerName}>{provider}</div>
            <div style={styles.providerMockWindow}>
              <span>{step1}</span>
              <span style={styles.providerArrow}>→</span>
              <span style={styles.providerShareButton}>{step2}</span>
              <span style={styles.providerArrow}>→</span>
              <span>{step3}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.infoSmall}>
        Share only the conversation relevant to this submission. Do not
        include unrelated private or personal information.
      </div>
    </div>
  );
}

function AiDisclosurePanel({
  aiUsed,
  setAiUsed,
  policyConfirmed,
  setPolicyConfirmed,
  conversationUrl,
  setConversationUrl,
  permittedAiUses
}) {
  const [showPolicyInfo, setShowPolicyInfo] = useState(false);
  const [showSharingInfo, setShowSharingInfo] = useState(false);

  function chooseAiUsed(nextValue) {
    setAiUsed(nextValue);

    if (!nextValue) {
      setPolicyConfirmed(false);
      setConversationUrl('');
      setShowPolicyInfo(false);
      setShowSharingInfo(false);
    }
  }

  return (
    <div style={styles.aiDisclosurePanel}>
      <div style={styles.aiDisclosureHeading}>AI Disclosure</div>

      <div style={styles.aiQuestion}>
        Did you use AI in creating this submission?
      </div>

      <div style={styles.radioRow}>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name="assignment-ai-used"
            checked={aiUsed === false}
            onChange={() => chooseAiUsed(false)}
          />
          No
        </label>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name="assignment-ai-used"
            checked={aiUsed === true}
            onChange={() => chooseAiUsed(true)}
          />
          Yes
        </label>
      </div>

      {aiUsed && (
        <div style={styles.aiYesArea}>
          {permittedAiUses.length === 0 && (
            <div style={styles.noAiAllowed}>
              This course does not permit any uses of AI for this
              assignment. You cannot submit with “Yes” selected.
            </div>
          )}

          <div style={styles.confirmationRow}>
            <span>
              I confirm that I used AI for this assignment in accordance
              with this course's AI Policy.
            </span>
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowPolicyInfo(prev => !prev)}
              aria-label="Show permitted AI uses"
              title="Show permitted AI uses"
            >
              ?
            </button>
            <input
              type="checkbox"
              checked={policyConfirmed}
              onChange={event =>
                setPolicyConfirmed(event.target.checked)
              }
            />
          </div>

          {showPolicyInfo && (
            <AiPolicyInfo permittedAiUses={permittedAiUses} />
          )}

          <div style={styles.urlRow}>
            <input
              type="url"
              style={styles.urlInput}
              value={conversationUrl}
              onChange={event =>
                setConversationUrl(event.target.value)
              }
              placeholder="Enter AI Conversation Sharing URL here"
            />
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowSharingInfo(prev => !prev)}
              aria-label="How to find a sharing URL"
              title="How to find a sharing URL"
            >
              ?
            </button>
          </div>

          {showSharingInfo && <SharingUrlInfo />}
        </div>
      )}
    </div>
  );
}

function AiDisclosureReadOnly({ submission }) {
  if (typeof submission?.ai_used !== 'boolean') return null;

  return (
    <div style={styles.aiDisclosureReadOnly}>
      <div style={styles.feedbackLabel}>AI Disclosure</div>
      <div>
        Used AI: <strong>{submission.ai_used ? 'Yes' : 'No'}</strong>
      </div>
      {submission.ai_used && submission.ai_conversation_url && (
        <div style={styles.aiDisclosureLinkRow}>
          Conversation sharing URL:{' '}
          <a
            href={submission.ai_conversation_url}
            target="_blank"
            rel="noreferrer"
          >
            {submission.ai_conversation_url}
          </a>
        </div>
      )}
    </div>
  );
}

export default function StudentAssignments({
  session,
  course,
  enrolment,
  onBack,
  selectedAssignmentTypeId,
  selectedSequenceId = null,
  selectedSequenceName = '',
  onOpenAssignmentType = null,
  onBackToSequence = null
}) {
  const [rawAssignments, setRawAssignments] = useState([]);
  const [typeDescriptions, setTypeDescriptions] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [structuredFormInstances, setStructuredFormInstances] = useState([]);
  const [assignmentTypeSettings, setAssignmentTypeSettings] = useState([]);

  const [
    structuredFormCheckpoints,
    setStructuredFormCheckpoints
  ] = useState([]);

  const [
    submissionFileSlots,
    setSubmissionFileSlots
  ] = useState([]);

  const [groupMemberships, setGroupMemberships] = useState([]);
  const [assignmentGroupRoster, setAssignmentGroupRoster] = useState([]);
  const [groupDeadlines, setGroupDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [feedbackResponse, setFeedbackResponse] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savingResponse, setSavingResponse] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [aiDisclosureSettings, setAiDisclosureSettings] = useState({});
  const [permittedAiUses, setPermittedAiUses] = useState([]);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiPolicyConfirmed, setAiPolicyConfirmed] = useState(false);
  const [aiConversationUrl, setAiConversationUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const refreshTick = useStudentDataRefreshTick();

  const groupRostersByAssignment = useMemo(() => {
    const result = {};

    groupMemberships.forEach(membership => {
      const members = assignmentGroupRoster
        .filter(row =>
          String(row.course_assignment_id) ===
            String(membership.course_assignment_id) &&
          Number(row.group_number) ===
            Number(membership.group_number) &&
          row.enrolments?.students?.is_master !== true
        )
        .map(row => row.enrolments)
        .filter(Boolean)
        .sort((a, b) =>
          String(a?.students?.student_code || '')
            .localeCompare(
              String(b?.students?.student_code || ''),
              undefined,
              {
                numeric: true,
                sensitivity: 'base'
              }
            )
        );

      result[membership.course_assignment_id] = {
        groupNumber: membership.group_number,
        members
      };
    });

    return result;
  }, [groupMemberships, assignmentGroupRoster]);

  const assignments = useMemo(
    () =>
      applyStudentAssignmentTiming({
        assignments: rawAssignments,
        assignmentTypeSettings,
        enrolments: [enrolment],
        memberships: groupMemberships,
        deadlines: groupDeadlines,
        submissions
      }),
    [
      rawAssignments,
      assignmentTypeSettings,
      enrolment,
      groupMemberships,
      groupDeadlines,
      submissions
    ]
  );

  useEffect(() => {
    Promise.all([
      fetchCourseAssignments(course.id),
      fetchTypeDescriptions(course.id),
      fetchSubmissions(enrolment.id),
      fetchAiDisclosureContext(course.id),
      fetchStudentAssignmentGroupMemberships(enrolment.id),
      fetchAssignmentGroupRoster(course.id),
      fetchStudentAssignmentGroupDeadlines(course.id),
      fetchStructuredFormInstances(course.id)
    ])
      .then(([
        a,
        td,
        s,
        aiContext,
        memberships,
        groupRoster,
        deadlines,
        structuredInstances
      ]) => {
        const displayNameByTypeId =
          new Map(
            (Array.isArray(aiContext.assignmentTypeSettings)
              ? aiContext.assignmentTypeSettings
              : []
            ).map(setting => [
              String(setting.assignment_type_id),
              String(setting.display_name || '').trim()
            ])
          );

        setRawAssignments(
          (Array.isArray(a) ? a : []).map(assignment => {
            const displayName =
              displayNameByTypeId.get(
                String(assignment.assignment_type_id)
              );

            if (!displayName) {
              return assignment;
            }

            return {
              ...assignment,
              assignment_types: {
                ...(assignment.assignment_types || {}),
                name: displayName
              }
            };
          })
        );

        const descMap = {};

        (Array.isArray(td) ? td : []).forEach(d => {
          descMap[d.assignment_type_id] = d.description;
        });

        setTypeDescriptions(descMap);
        setSubmissions(Array.isArray(s) ? s : []);
        setAiDisclosureSettings(aiContext.settingsMap || {});
        setPermittedAiUses(aiContext.permittedAiUses || []);
        setAssignmentTypeSettings(
          aiContext.assignmentTypeSettings || []
        );
        setGroupMemberships(
          Array.isArray(memberships) ? memberships : []
        );
        setAssignmentGroupRoster(
          Array.isArray(groupRoster) ? groupRoster : []
        );
        setGroupDeadlines(
          Array.isArray(deadlines) ? deadlines : []
        );
        setStructuredFormInstances(
          Array.isArray(structuredInstances) ? structuredInstances : []
        );
      })
      .finally(() => setLoading(false));
  }, [course.id, enrolment.id, refreshTick]);


  useEffect(() => {
    let cancelled = false;

    const hasStructuredForms =
      rawAssignments.some(
        assignment =>
          assignment
            ?.assignment_types
            ?.assignment_kind ===
          'structured_form'
      );

    if (!hasStructuredForms) {
      setStructuredFormCheckpoints(
        []
      );

      return () => {
        cancelled = true;
      };
    }

    loadStudentStructuredFormCheckpoints({
      courseId:
        course.id,

      enrolment: {
        id:
          enrolment.id
      },

      assignments:
        rawAssignments,

      assignmentTypeSettings:
        assignmentTypeSettings,

      isMaster:
        !!session?.isMaster
    })
      .then(rows => {
        if (!cancelled) {
          setStructuredFormCheckpoints(
            Array.isArray(rows)
              ? rows
              : []
          );
        }
      })
      .catch(err => {
        console.error(
          'Loading Structured Form instance timing failed:',
          err
        );

        if (!cancelled) {
          setStructuredFormCheckpoints(
            []
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    course.id,
    enrolment.id,
    rawAssignments,
    assignmentTypeSettings,
    session?.isMaster,
    refreshTick
  ]);

  useEffect(() => {
    let cancelled = false;

    fetchSubmissionFileSlots(
      course.id
    )
      .then(rows => {
        if (!cancelled) {
          setSubmissionFileSlots(
            Array.isArray(rows)
              ? rows
              : []
          );
        }
      })
      .catch(error => {
        if (!cancelled) {
          console.error(
            'Loading submission file slots failed:',
            error
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    course.id,
    refreshTick
  ]);


  useEffect(() => {
    if (!selectedAssignment) return;

    const refreshed = assignments.find(
      assignment => assignment.id === selectedAssignment.id
    );

    if (refreshed) {
      setSelectedAssignment(refreshed);
    }
    // The selected assignment should refresh whenever the resolved
    // assignment list changes (for example, after an extension refresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);

  useEffect(() => {
    async function refreshSubmissionState() {
      try {
        const latest = await fetchSubmissions(enrolment.id);
        setSubmissions(Array.isArray(latest) ? latest : []);
      } catch (err) {
        console.error('Failed to refresh assignment submissions:', err);
      }
    }

    window.addEventListener('focus', refreshSubmissionState);

    return () => {
      window.removeEventListener('focus', refreshSubmissionState);
    };
  }, [enrolment.id]);

  useEffect(() => {
  if (!selectedAssignment) return;

  const current = submissions.find(
    s =>
      s.course_assignment_id === selectedAssignment.id &&
      !s.course_assignment_schedule_item_id &&
      !s.structured_form_instance_id
  );

  if (!current?.submitted) {
    setSuccess(prev =>
      prev === 'Submitted successfully.' ||
      prev === 'Response submitted.'
        ? ''
        : prev
    );
  }
}, [submissions, selectedAssignment]);

  function loadDisclosureFromSubmission(submission) {
    if (typeof submission?.ai_used === 'boolean') {
      setAiUsed(submission.ai_used);
      setAiPolicyConfirmed(!!submission.ai_policy_confirmed);
      setAiConversationUrl(submission.ai_conversation_url || '');
    } else {
      setAiUsed(false);
      setAiPolicyConfirmed(false);
      setAiConversationUrl('');
    }
  }

  const applicableAssignments = assignments.filter(
    assignment =>
      session.isMaster ||
      assignment.student_assignment_applicable !== false
  );

  const sequenceTypeIds = new Set(
    selectedSequenceId
      ? assignmentTypeSettings
          .filter(
            setting =>
              String(setting.sequence_id || '') ===
              String(selectedSequenceId)
          )
          .map(setting =>
            String(setting.assignment_type_id)
          )
      : []
  );

  const menuOrderByTypeId = new Map(
    assignmentTypeSettings.map(setting => [
      String(setting.assignment_type_id),
      Number.isFinite(Number(setting.menu_order))
        ? Number(setting.menu_order)
        : Number.MAX_SAFE_INTEGER
    ])
  );

  const filteredAssignments = (
    selectedAssignmentTypeId
      ? applicableAssignments.filter(
          assignment =>
            String(assignment.assignment_type_id) ===
            String(selectedAssignmentTypeId)
        )
      : selectedSequenceId
        ? applicableAssignments.filter(
            assignment =>
              sequenceTypeIds.has(
                String(assignment.assignment_type_id)
              )
          )
        : applicableAssignments
  ).slice().sort((a, b) => {
    if (!selectedSequenceId) return 0;

    const aOrder =
      menuOrderByTypeId.get(
        String(a.assignment_type_id)
      ) ?? Number.MAX_SAFE_INTEGER;

    const bOrder =
      menuOrderByTypeId.get(
        String(b.assignment_type_id)
      ) ?? Number.MAX_SAFE_INTEGER;

    return (
      aOrder - bOrder ||
      (Number(a.instance_number) || 0) -
        (Number(b.instance_number) || 0)
    );
  });

  const sequenceTypeMenu =
    !!selectedSequenceId &&
    !selectedAssignmentTypeId;

  const visibleAssignments =
    sequenceTypeMenu
      ? filteredAssignments.filter(
          (assignment, index, rows) =>
            rows.findIndex(
              candidate =>
                String(
                  candidate.assignment_type_id
                ) ===
                String(
                  assignment.assignment_type_id
                )
            ) === index
        )
      : filteredAssignments;

  function getSubmission(assignmentId) {
    return submissions.find(
      s =>
        s.course_assignment_id === assignmentId &&
        !s.course_assignment_schedule_item_id &&
        !s.structured_form_instance_id
    );
  }

  function getResolvedAssignment(
    assignmentId
  ) {
    return (
      assignments.find(
        assignment =>
          String(
            assignment.id
          ) ===
          String(
            assignmentId
          )
      ) ||
      selectedAssignment ||
      null
    );
  }

  function getStructuredSnapshots(assignmentId) {
    return submissions.filter(
      s =>
        s.course_assignment_id === assignmentId &&
        !!s.structured_form_instance_id &&
        s.submitted === true
    );
  }


  function getNextStructuredCheckpoint(
    assignmentId,
    snapshots
  ) {
    const submittedInstanceIds =
      new Set(
        (
          Array.isArray(snapshots)
            ? snapshots
            : []
        )
          .map(
            row =>
              row
                .structured_form_instance_id
          )
          .filter(Boolean)
          .map(String)
      );

    const candidates =
      structuredFormCheckpoints
        .filter(
          checkpoint =>
            String(
              checkpoint
                .course_assignment_id
            ) ===
              String(
                assignmentId
              ) &&
            checkpoint
              .student_assignment_applicable !==
              false &&
            !submittedInstanceIds.has(
              String(
                checkpoint
                  .structured_form_instance_id
              )
            )
        );

    const byInstanceOrder =
      (a, b) =>
        Number(
          a
            .structured_form_instance_order ||
          0
        ) -
        Number(
          b
            .structured_form_instance_order ||
          0
        );

    if (
      isOpenEverything(
        session
      )
    ) {
      return (
        candidates
          .slice()
          .sort(
            byInstanceOrder
          )[0] ||
        null
      );
    }

    const today =
      getEffectiveToday(
        session
      );

    const active =
      candidates
        .filter(
          checkpoint => {
            const openDate =
              String(
                checkpoint
                  .effective_open_date ||
                checkpoint
                  .open_date ||
                ''
              ).slice(0, 10);

            const dueDate =
              String(
                checkpoint
                  .effective_due_date ||
                checkpoint
                  .due_date ||
                checkpoint
                  .close_date ||
                ''
              ).slice(0, 10);

            return (
              openDate &&
              today >= openDate &&
              (
                !dueDate ||
                today <= dueDate
              )
            );
          }
        )
        .sort(
          byInstanceOrder
        )[0];

    if (active) {
      return active;
    }

    return (
      candidates
        .filter(
          checkpoint => {
            const openDate =
              String(
                checkpoint
                  .effective_open_date ||
                checkpoint
                  .open_date ||
                ''
              ).slice(0, 10);

            return (
              openDate &&
              openDate > today
            );
          }
        )
        .sort(
          (a, b) => {
            const aDate =
              String(
                a
                  .effective_open_date ||
                a.open_date ||
                ''
              ).slice(0, 10);

            const bDate =
              String(
                b
                  .effective_open_date ||
                b.open_date ||
                ''
              ).slice(0, 10);

            return (
              aDate.localeCompare(
                bDate
              ) ||
              byInstanceOrder(
                a,
                b
              )
            );
          }
        )[0] ||
      null
    );
  }

  function getLabel(assignment) {
    const typeName =
      assignment
        ?.assignment_types
        ?.name ||
      'Assignment';

    const sameType =
      assignments.filter(
        candidate =>
          String(
            candidate.assignment_type_id
          ) ===
          String(
            assignment.assignment_type_id
          )
      );

    return resolveAssignmentInstanceLabel(
      assignment,
      {
        baseName: typeName,
        sameTypeCount:
          sameType.length
      }
    );
  }

  function getDescription(assignment) {
    return (
      typeDescriptions[assignment.assignment_type_id] ||
      assignment.assignment_types?.instructions ||
      ''
    );
  }

  function buildFileName(assignment, ext) {
    const label = getLabel(assignment);
    const studentId = session.studentCode || session.studentId;

    return `${label} - ${studentId}.${ext}`;
  }

    async function handleSelectAssignment(assignment) {
    setError('');
    setSuccess('');
    setFeedbackResponse('');
    setSelectedFile(null);
    setAiUsed(false);
    setAiPolicyConfirmed(false);
    setAiConversationUrl('');

    try {
      const latest = await fetchSubmissions(enrolment.id);

      const latestSubmissions =
        Array.isArray(latest) ? latest : [];

      setSubmissions(latestSubmissions);

      const current = latestSubmissions.find(
        s => s.course_assignment_id === assignment.id
      );

      setFeedbackResponse(
        current?.assignment_feedback?.student_response || ''
      );
      loadDisclosureFromSubmission(current);
    } catch (err) {
      console.error(
        'Failed to refresh assignment submission:',
        err
      );

      const current = getSubmission(assignment.id);

      setFeedbackResponse(
        current?.assignment_feedback?.student_response || ''
      );
      loadDisclosureFromSubmission(current);
    }

    setSelectedAssignment(assignment);
  }

  async function handleFeedbackResponse(e) {
    e.preventDefault();

    setError('');
    setSuccess('');

    const submission =
      getSubmission(selectedAssignment.id);

    const feedback =
      submission?.assignment_feedback;

    if (
      !submission?.submitted ||
      feedback?.submitted !== true
    ) {
      setError(
        'Instructor feedback is no longer available.'
      );
      return;
    }

    if (!hasMeaningfulRichText(feedbackResponse)) {
      setError('Please write a response.');
      return;
    }

    setSavingResponse(true);

    try {
      const updatedFeedback =
        await submitFeedbackResponse(
          submission.id,
          feedbackResponse.trim()
        );

      setSubmissions(prev =>
        prev.map(s =>
          s.id === submission.id
            ? {
                ...s,
                assignment_feedback: {
                  ...s.assignment_feedback,
                  ...updatedFeedback
                }
              }
            : s
        )
      );

      setFeedbackResponse(
        updatedFeedback.student_response ||
        feedbackResponse.trim()
      );

      setSuccess('Response submitted.');
    } catch (err) {
      console.error(
        'Failed to submit assignment feedback response:',
        err
      );

      setError(
        err.message ||
        'Failed to submit response.'
      );
    } finally {
      setSavingResponse(false);
    }
  }

  function submissionTypeSettingFor(
    assignment
  ) {
    return (
      assignmentTypeSettings
        .find(
          setting =>
            String(
              setting.assignment_type_id
            ) ===
            String(
              assignment
                ?.assignment_type_id ||
              ''
            )
        ) ||
      {
        allowed_pdf:
          true,

        allowed_docx:
          true,

        multi_file_submission:
          false
      }
    );
  }


  async function ensureSubmissionForAssignment(
    assignment
  ) {
    const current =
      getSubmission(
        assignment.id
      );

    if (current?.id) {
      return current;
    }

    const rows =
      await createSubmission(
        enrolment.id,
        assignment.id
      );

    if (
      !Array.isArray(rows) ||
      !rows[0]
    ) {
      throw new Error(
        'Failed to create submission.'
      );
    }

    setSubmissions(
      previous => [
        ...previous,
        rows[0]
      ]
    );

    return rows[0];
  }


  function getMultiFileDisclosureData(
    disclosureEnabled
  ) {
    if (
      disclosureEnabled &&
      aiUsed
    ) {
      if (
        permittedAiUses.length ===
        0
      ) {
        throw new Error(
          'This course does not permit any uses of AI for this assignment.'
        );
      }

      if (!aiPolicyConfirmed) {
        throw new Error(
          'You must confirm that your AI use complied with the course AI Policy.'
        );
      }

      if (
        !isValidHttpsUrl(
          aiConversationUrl
        )
      ) {
        throw new Error(
          'Enter a valid HTTPS AI conversation sharing URL.'
        );
      }
    }

    return disclosureEnabled
      ? {
          ai_used:
            !!aiUsed,

          ai_policy_confirmed:
            aiUsed
              ? !!aiPolicyConfirmed
              : false,

          ai_conversation_url:
            aiUsed
              ? aiConversationUrl.trim()
              : null
        }
      : {};
  }


   async function handleFileSelect(file) {
    if (!file) return;

    try {
      await validateConfiguredSubmissionFile(
        file,
        submissionTypeSettingFor(
          selectedAssignment
        )
      );

      setSelectedFile(
        file
      );

      setError('');

    } catch (err) {
      setSelectedFile(
        null
      );

      setError(
        err?.message ||
        'That file cannot be submitted.'
      );
    }
  }

  async function handleSubmitSelectedFile() {
    if (!selectedFile) {
      setError('Choose a file to submit.');
      return;
    }

    await handleFileUpload(selectedFile);
  }

  async function handleFileUpload(file) {
    if (!file) return;

    if (!isInlineReviewableFile(file)) {
      setSelectedFile(null);

      setError(
        'Only PDF or Word (.docx) files can be submitted.'
      );

      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError(
        'File too large. Maximum size is 50MB.'
      );
      return;
    }

    const currentAssignment =
      getResolvedAssignment(
        selectedAssignment.id
      );

    if (!currentAssignment) {
      setError(
        'Could not resolve the current assignment.'
      );
      return;
    }

    const existingSubmission =
      getSubmission(
        currentAssignment.id
      );

    const status =
      getAssignmentStatus(
        currentAssignment,
        session
      );

    if (status !== 'open') {
      setError(
        'The submission window is not currently open.'
      );
      return;
    }

    const disclosureEnabled =
      !!aiDisclosureSettings[
        currentAssignment
          .assignment_type_id
      ];

    if (disclosureEnabled && aiUsed) {
      if (permittedAiUses.length === 0) {
        setError(
          'This course does not permit any uses of AI for this assignment.'
        );
        return;
      }

      if (!aiPolicyConfirmed) {
        setError(
          'You must confirm that your AI use complied with the course AI Policy.'
        );
        return;
      }

      if (!isValidHttpsUrl(aiConversationUrl)) {
        setError(
          'Enter a valid HTTPS AI conversation sharing URL.'
        );
        return;
      }
    }

    const disclosureData = disclosureEnabled
      ? {
          ai_used: !!aiUsed,
          ai_policy_confirmed: aiUsed
            ? !!aiPolicyConfirmed
            : false,
          ai_conversation_url: aiUsed
            ? aiConversationUrl.trim()
            : null
        }
      : {};

    setUploading(true);
    setError('');

    try {
      const ext = file.name.split('.').pop();

      const fileName =
        buildFileName(
          currentAssignment,
          ext
        );

      let submission = existingSubmission;

      if (!submission) {
        const result =
          await createSubmission(
            enrolment.id,
            currentAssignment.id
          );

        if (!Array.isArray(result) || !result[0]) {
          throw new Error(
            'Failed to create submission.'
          );
        }

        submission = result[0];

        setSubmissions(prev => [
          ...prev,
          submission
        ]);
      }

      const previousDriveFileId =
        submission.drive_file_id || null;

      const previousFileName =
        submission.file_name || null;

      await updateSubmission(
        submission.id,
        {
          submitted: false,
          submitted_at: null,
          drive_file_id: null,
          file_name: null
        }
      );

      try {
        const base64Data =
          await new Promise(
            (resolve, reject) => {
              const reader =
                new FileReader();

              reader.onload = event => {
                const data = String(
                  event.target?.result || ''
                );

                resolve(
                  data.includes(',')
                    ? data.split(',')[1]
                    : data
                );
              };

              reader.onerror = () =>
                reject(
                  new Error(
                    'Could not read the selected file.'
                  )
                );

              reader.readAsDataURL(file);
            }
          );

        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type':
              'application/json'
          },
          body: JSON.stringify({
            action:
              'uploadAssignmentSubmission',
            submissionId:
              submission.id,
            courseId:
              course.id,
            fileName,
            base64Data,
            mimeType:
              file.type
          })
        });

        let driveFileId = null;

        for (
          let i = 0;
          i < 30;
          i += 1
        ) {
          await new Promise(resolve =>
            setTimeout(resolve, 2000)
          );

          const checkResponse =
            await fetch(
              SUPABASE_URL +
                '/rest/v1/assignment_submissions?id=eq.' +
                submission.id +
                '&select=drive_file_id',
              {
                headers: {
                  'apikey':
                    SUPABASE_KEY,

                  'Authorization':
                    'Bearer ' +
                    SUPABASE_KEY
                }
              }
            );

          if (!checkResponse.ok) {
            throw new Error(
              'Could not verify the uploaded file.'
            );
          }

          const checkData =
            await checkResponse.json();

          if (
            checkData?.[0]
              ?.drive_file_id
          ) {
            driveFileId =
              checkData[0]
                .drive_file_id;

            break;
          }
        }

        if (!driveFileId) {
          throw new Error(
            'The file upload did not complete. Your assignment has not been submitted. Please try again.'
          );
        }

        const updatedRows =
          await updateSubmission(
            submission.id,
            {
              submitted: true,

              submitted_at:
                new Date()
                  .toISOString(),

              file_name:
                fileName,

              drive_file_id:
                driveFileId,

              submitted_by_instructor_id:
                null,

              ...disclosureData
            }
          );

        const updatedSubmission =
          Array.isArray(
            updatedRows
          )
            ? updatedRows[0]
            : null;

        if (!updatedSubmission) {
          throw new Error(
            'The file uploaded, but the submission could not be finalized.'
          );
        }

        setSubmissions(prev =>
          prev.map(s =>
            s.id === submission.id
              ? {
                  ...s,
                  ...updatedSubmission
                }
              : s
          )
        );

        setSelectedFile(null);

        setSuccess(
          'Submitted successfully.'
        );

        setUploading(false);

      } catch (uploadError) {
        try {
          await updateSubmission(
            submission.id,
            {
              submitted: false,
              submitted_at: null,

              drive_file_id:
                previousDriveFileId,

              file_name:
                previousFileName
            }
          );

        } catch (restoreError) {
          console.error(
            'Could not restore previous submission metadata:',
            restoreError
          );
        }

        throw uploadError;
      }
    } catch (err) {
      setError(
        err.message ||
        'Failed to submit.'
      );

      setUploading(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Loading...
        </div>
      </div>
    );
  }
const selectedScheduleSetting = selectedAssignmentTypeId
    ? assignmentTypeSettings.find(
        setting =>
          String(setting.assignment_type_id) ===
          String(selectedAssignmentTypeId)
      )
    : null;

  const selectedAssignmentKind = selectedAssignmentTypeId
    ? rawAssignments.find(
        assignment =>
          String(assignment.assignment_type_id) ===
          String(selectedAssignmentTypeId)
      )?.assignment_types?.assignment_kind || 'standard'
    : 'standard';

  if (
    selectedAssignmentTypeId &&
    selectedAssignmentKind !== 'peer_review' &&
    selectedAssignmentKind !== 'structured_form' &&
    selectedScheduleSetting?.schedule_enabled === true &&
    rawAssignments.some(assignment =>
      String(assignment.assignment_type_id) ===
        String(selectedAssignmentTypeId) &&
      assignment.assignment_types?.requires_submission !== false
    )
  ) {
    const scheduledAssignments = rawAssignments.filter(
      assignment =>
        String(assignment.assignment_type_id) ===
        String(selectedAssignmentTypeId)
    );

    const scheduledDescription =
      typeDescriptions[selectedAssignmentTypeId] ||
      scheduledAssignments[0]?.assignment_types?.instructions ||
      '';

    return (
      <StudentScheduledAssignments
        session={session}
        course={course}
        enrolment={enrolment}
        assignments={scheduledAssignments}
        assignmentTypeSetting={selectedScheduleSetting}
        description={scheduledDescription}
        aiDisclosureEnabled={
          !!aiDisclosureSettings[selectedAssignmentTypeId]
        }
        permittedAiUses={permittedAiUses}
        allCourseAssignments={rawAssignments}
        groupRostersByAssignment={groupRostersByAssignment}
        submissionFileSlots={
          submissionFileSlots.filter(
            slot =>
              String(
                slot.assignment_type_id
              ) ===
              String(
                selectedAssignmentTypeId
              )
          )
        }
      />
    );
  }

  if (filteredAssignments.length === 0) {
    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          {selectedSequenceId &&
          selectedAssignmentTypeId &&
          onBackToSequence && (
            <button
              type="button"
              style={styles.backLink}
              onClick={onBackToSequence}
            >
              ← {selectedSequenceName || 'Assignment Sequence'}
            </button>
          )}

        <h2 style={styles.heading}>
            Other Assignments
          </h2>

          <div style={styles.muted}>
            No assignments for this course.
          </div>
        </div>
      </div>
    );
  }

  if (
    selectedAssignment?.assignment_types?.assignment_kind ===
    'structured_form'
  ) {
    const structuredSubmission =
      getSubmission(
        selectedAssignment.id
      );

    const structuredDescription =
      getDescription(
        selectedAssignment
      );

    const structuredTypeSetting =
      assignmentTypeSettings.find(
        setting =>
          String(
            setting.assignment_type_id
          ) ===
          String(
            selectedAssignment
              .assignment_type_id
          )
      ) || null;

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button
            style={styles.backLink}
            onClick={() => {
              setSelectedAssignment(null);
              setError('');
              setSuccess('');
            }}
          >
            ← Back to assignments
          </button>

          <h2 style={styles.heading}>
            {getLabel(
              selectedAssignment
            )}
          </h2>

          <AssignmentInstructions
            html={structuredDescription}
          />

          <StudentStructuredForm
            key={
              selectedAssignment.id
            }
            session={session}
            assignment={
              selectedAssignment
            }
            enrolment={enrolment}
            submission={
              structuredSubmission
            }
            snapshotSubmissions={
              getStructuredSnapshots(
                selectedAssignment.id
              )
            }
            aiDisclosureEnabled={
              !!aiDisclosureSettings[
                selectedAssignment
                  .assignment_type_id
              ]
            }
            permittedAiUses={
              permittedAiUses
            }
            gradingMode={
              course?.grading_mode
            }
            scheduleEnabled={
              !!structuredTypeSetting
                ?.schedule_enabled
            }
            scheduleChoiceEnabled={
              !!structuredTypeSetting
                ?.schedule_choice_enabled
            }
            scheduleChoiceOpenDate={
              structuredTypeSetting
                ?.schedule_choice_open_date ||
              ''
            }
            scheduleChoiceCloseDate={
              structuredTypeSetting
                ?.schedule_choice_close_date ||
              ''
            }
            groupDeadlinesEnabled={
              !!structuredTypeSetting
                ?.group_deadlines_enabled
            }
            onSubmissionChanged={
              saved =>
                setSubmissions(
                  prev => {
                    const exists =
                      prev.some(
                        row =>
                          row.id ===
                          saved.id
                      );

                    return exists
                      ? prev.map(
                          row =>
                            row.id ===
                            saved.id
                              ? {
                                  ...row,
                                  ...saved
                                }
                              : row
                        )
                      : [
                          ...prev,
                          saved
                        ];
                  }
                )
            }
          />

          <AssignmentGroupMembers
            group={
              groupRostersByAssignment[
                selectedAssignment.id
              ] || null
            }
            assignmentId={
              selectedAssignment.id
            }
            courseId={course.id}
            session={session}
          />
        </div>
      </div>
    );
  }

   if (
    selectedAssignment?.assignment_types?.assignment_kind ===
    'peer_review'
  ) {
    const peerReviewSubmission =
      getSubmission(selectedAssignment.id);

    const peerReviewStatus = getAssignmentStatus(
      selectedAssignment,
      session,
      peerReviewSubmission
    );

    const peerReviewDescription =
      getDescription(selectedAssignment);

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button
            style={styles.backLink}
            onClick={() => {
              setSelectedAssignment(null);
              setError('');
              setSuccess('');
            }}
          >
            ← Back to assignments
          </button>

          <h2 style={styles.heading}>
            {getLabel(selectedAssignment)}
          </h2>

          <AssignmentInstructions
            html={peerReviewDescription}
          />

          <StudentPeerReview
            courseAssignment={selectedAssignment}
            enrolment={enrolment}
            status={peerReviewStatus}
          />
        </div>
      </div>
    );
  }

  if (selectedAssignment) {
    const currentAssignment =
      getResolvedAssignment(
        selectedAssignment.id
      );

    const requiresSubmission =
      currentAssignment
        ?.assignment_types
        ?.requires_submission ??
      true;

    const submission =
      getSubmission(
        currentAssignment?.id ||
        selectedAssignment.id
      );

    const status =
      getAssignmentStatus(
        currentAssignment ||
          selectedAssignment,
        session
      );

    const isSubmitted =
      submission?.submitted;

    const feedback =
      submission?.assignment_feedback;

    const hasFeedback =
      isSubmitted &&
      feedback?.submitted === true;

    const description =
      getDescription(selectedAssignment);

    const aiDisclosureEnabled =
      !!aiDisclosureSettings[
        selectedAssignment.assignment_type_id
      ];

    const submissionFileSetting =
      submissionTypeSettingFor(
        selectedAssignment
      );

    const submissionFileConfig =
      normalizeSubmissionFileSettings(
        submissionFileSetting
      );

    const multiFileEnabled =
      submissionFileConfig
        .multiFile;

    const currentSubmissionFileSlots =
      submissionFileSlots.filter(
        slot =>
          String(
            slot.assignment_type_id
          ) ===
          String(
            selectedAssignment
              .assignment_type_id
          )
      );

    const extensionDays =
      (
        currentAssignment ||
        selectedAssignment
      ).effective_extension_days ||
      0;

    const baseDueDate =
      getStudentAssignmentBaseDueDate(
        currentAssignment ||
          selectedAssignment
      );

    const effectiveDueDate =
      getStudentAssignmentDueDate(
        currentAssignment ||
          selectedAssignment
      );

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button
            style={styles.backLink}
            onClick={() => {
              setSelectedAssignment(null);
              setFeedbackResponse('');
              setSelectedFile(null);
              setAiUsed(false);
              setAiPolicyConfirmed(false);
              setAiConversationUrl('');
              setError('');
              setSuccess('');
            }}
          >
            ← Back to assignments
          </button>

          <h2 style={styles.heading}>
            {getLabel(selectedAssignment)}
          </h2>

          <AssignmentInstructions
            html={description}
          />

          {baseDueDate &&
            requiresSubmission && (
              <div style={styles.dueBanner}>
                <div>
                  Due:{' '}
                  {formatDate(
                    effectiveDueDate ||
                    baseDueDate
                  )}

                  {selectedAssignment.word_limit &&
                    ` · Word limit: ${selectedAssignment.word_limit}`}
                </div>

                {extensionDays > 0 && (
                  <div style={styles.extensionNotice}>
                    <strong>
                      {extensionDays}-day extension
                    </strong>
                    {' · '}
                    Original deadline:{' '}
                    {formatDate(baseDueDate)}
                  </div>
                )}
              </div>
            )}

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {success && (
            <div style={styles.success}>
              {success}
            </div>
          )}

<ReceivedPeerReviews
            targetSubmissionId={submission?.id}
          />

         <ForwardedAssignmentFeedback
            destinationAssignmentId={selectedAssignment.id}
            allAssignments={rawAssignments}
            submissions={submissions}
          />

          {hasFeedback && (
            <div style={styles.feedbackBox}>
              <StudentInstructorFeedback
                submissionType="assignment"
                submissionId={
                  submission?.id
                }
                enrolmentId={
                  enrolment.id
                }
                effectiveFeedback={
                  feedback
                }
                gradingMode={
                  course?.grading_mode
                }
              />
            </div>
          )}

          {hasFeedback &&
            !feedback.student_response && (
              <form
                onSubmit={handleFeedbackResponse}
                style={styles.responseForm}
              >
                <label style={styles.responseLabel}>
                  Your response to instructor feedback
                </label>

                <div style={styles.responseHint}>
                  Optional
                </div>

                <RichTextEditor
                  value={feedbackResponse}
                  onChange={setFeedbackResponse}
                />

                <button
                  type="submit"
                  style={styles.responseBtn}
                  disabled={savingResponse}
                >
                  {savingResponse
                    ? 'Submitting...'
                    : 'Submit Response'}
                </button>
              </form>
            )}

          {hasFeedback &&
            feedback.student_response && (
              <div style={styles.responseBox}>
                <div style={styles.feedbackLabel}>
                  Your response
                </div>

                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      feedback.student_response
                  }}
                  style={styles.feedbackText}
                />
              </div>
            )}

          {requiresSubmission && (
            <div style={styles.uploadBox}>
              {multiFileEnabled ? (
                <>
                  {isSubmitted && (
                    <AiDisclosureReadOnly
                      submission={
                        submission
                      }
                    />
                  )}

                  <MultiFileSubmissionControls
                    submission={
                      submission
                    }
                    slots={
                      currentSubmissionFileSlots
                    }
                    courseId={
                      course.id
                    }
                    allowPdfImages={
  submissionFileConfig
    .allowPdfImages
}
                    status={
                      status
                    }
                    ensureSubmission={
                      () =>
                        ensureSubmissionForAssignment(
                          currentAssignment ||
                          selectedAssignment
                        )
                    }
                    buildStoredFileName={
                      (
                        slot,
                        file
                      ) => {
                        const ext =
                          getSubmissionFileExtension(
                            file
                          ) ||
                          'file';

                        const label =
                          getLabel(
                            currentAssignment ||
                            selectedAssignment
                          );

                        const studentId =
                          session.studentCode ||
                          session.studentId;

                        return (
                          safeSubmissionFilePart(
                            label
                          ) +
                          ' - ' +
                          safeSubmissionFilePart(
                            slot.label
                          ) +
                          ' - ' +
                          safeSubmissionFilePart(
                            studentId
                          ) +
                          '.' +
                          ext
                        );
                      }
                    }
                    getFinalizeData={
                      () => ({
                        ...getMultiFileDisclosureData(
                          aiDisclosureEnabled
                        ),

                        submitted_by_instructor_id:
                          null
                      })
                    }
                    onSubmitted={
                      saved =>
                        setSubmissions(
                          previous => {
                            const exists =
                              previous.some(
                                row =>
                                  row.id ===
                                  saved.id
                              );

                            return exists
                              ? previous.map(
                                  row =>
                                    row.id ===
                                    saved.id
                                      ? {
                                          ...row,
                                          ...saved
                                        }
                                      : row
                                )
                              : [
                                  ...previous,
                                  saved
                                ];
                          }
                        )
                    }
                  >
                    {aiDisclosureEnabled && (
                      <AiDisclosurePanel
                        aiUsed={
                          aiUsed
                        }
                        setAiUsed={
                          setAiUsed
                        }
                        policyConfirmed={
                          aiPolicyConfirmed
                        }
                        setPolicyConfirmed={
                          setAiPolicyConfirmed
                        }
                        conversationUrl={
                          aiConversationUrl
                        }
                        setConversationUrl={
                          setAiConversationUrl
                        }
                        permittedAiUses={
                          permittedAiUses
                        }
                      />
                    )}
                  </MultiFileSubmissionControls>

                  {isSubmitted &&
                    status ===
                      'open' && (
                    <div
                      style={
                        styles.lockedNote
                      }
                    >
                      Your submission is locked.
                      Contact your instructor to
                      resubmit.
                    </div>
                  )}
                </>

              ) : (
                <>
                  {isSubmitted ? (
                    <div
                      style={
                        styles.submittedInfo
                      }
                    >
                      <div
                        style={
                          styles.submittedBanner
                        }
                      >
                        ✓ Submitted
                      </div>
                    </div>
                  ) : (
                    <div
                      style={
                        styles.notSubmitted
                      }
                    >
                      No file submitted yet.
                    </div>
                  )}

                  {isSubmitted && (
                    <AiDisclosureReadOnly
                      submission={
                        submission
                      }
                    />
                  )}

                  {status ===
                    'open' &&
                    !isSubmitted && (
                    <>
                      <label
                        style={
                          styles.uploadLabel
                        }
                      >
                        <input
                          type="file"
                          accept={
                            getSubmissionFileAccept(
                              submissionFileSetting
                            )
                          }
                          style={{
                            display:
                              'none'
                          }}
                          onChange={
                            event =>
                              handleFileSelect(
                                event
                                  .target
                                  .files[0]
                              )
                          }
                          disabled={
                            uploading
                          }
                        />

                        <span
                          style={
                            styles.uploadBtn
                          }
                        >
                          {uploading
                            ? 'Uploading...'
                            : 'Choose submission file'}
                        </span>
                      </label>

                      <div
                        style={{
                          marginTop:
                            '6px',
                          color:
                            '#829ab1',
                          fontSize:
                            '11px'
                        }}
                      >
                        Accepted:{' '}
                        {
                          getSubmissionFileFormatLabel(
                            submissionFileSetting
                          )
                        }
                      </div>

                      {selectedFile && (
                        <div
                          style={
                            styles.selectedFile
                          }
                        >
                          Selected file:{' '}
                          <strong>
                            {
                              selectedFile.name
                            }
                          </strong>
                        </div>
                      )}

                      {aiDisclosureEnabled && (
                        <AiDisclosurePanel
                          aiUsed={
                            aiUsed
                          }
                          setAiUsed={
                            setAiUsed
                          }
                          policyConfirmed={
                            aiPolicyConfirmed
                          }
                          setPolicyConfirmed={
                            setAiPolicyConfirmed
                          }
                          conversationUrl={
                            aiConversationUrl
                          }
                          setConversationUrl={
                            setAiConversationUrl
                          }
                          permittedAiUses={
                            permittedAiUses
                          }
                        />
                      )}

                      <button
                        type="button"
                        style={
                          styles.submitFileBtn
                        }
                        disabled={
                          uploading ||
                          !selectedFile ||
                          (
                            aiDisclosureEnabled &&
                            aiUsed &&
                            permittedAiUses.length ===
                              0
                          )
                        }
                        onClick={
                          handleSubmitSelectedFile
                        }
                      >
                        {uploading
                          ? 'Submitting...'
                          : 'Submit'}
                      </button>
                    </>
                  )}

                  {status ===
                    'open' &&
                    isSubmitted && (
                    <div
                      style={
                        styles.lockedNote
                      }
                    >
                      Your submission is locked.
                      Contact your instructor to
                      resubmit.
                    </div>
                  )}

                  {status !==
                    'open' &&
                    !isSubmitted && (
                    <div
                      style={
                        styles.closedBanner
                      }
                    >
                      {status ===
                        'not_open'
                        ? 'Submissions not yet open.'
                        : status ===
                            'closed'
                          ? 'Submission window has closed.'
                          : 'No submission dates configured.'}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <AssignmentGroupMembers
            group={
              groupRostersByAssignment[
                selectedAssignment.id
              ] || null
            }
            assignmentId={selectedAssignment.id}
            courseId={course.id}
            session={session}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>
          {selectedSequenceId && !selectedAssignmentTypeId
            ? selectedSequenceName || 'Assignment Sequence'
            : filteredAssignments[0]
                ?.assignment_types?.name ||
              'Other Assignments'}
        </h2>

        <div style={styles.assignmentList}>
          {visibleAssignments.map(
            assignment => {
              const requiresSubmission =
                assignment.assignment_types
                  ?.requires_submission ?? true;

              const submission =
                getSubmission(assignment.id);

              const status =
                getAssignmentStatus(
                  assignment,
                  session
                );

              const isSubmitted =
                submission?.submitted;

              const hasFeedback =
                isSubmitted &&
                submission
                  ?.assignment_feedback
                  ?.submitted === true;

              const isStructuredForm =
                assignment.assignment_types?.assignment_kind ===
                'structured_form';

              const formInstances = isStructuredForm
                ? structuredFormInstances.filter(instance =>
                    instance.course_assignment_id === assignment.id
                  )
                : [];

              const formSnapshots = isStructuredForm
                ? getStructuredSnapshots(assignment.id)
                : [];

              const formFeedbackCount = formSnapshots.filter(snapshot =>
                snapshot.assignment_feedback?.submitted
              ).length;

              const label =
                sequenceTypeMenu
                  ? (
                      assignment
                        .assignment_types
                        ?.name ||
                      'Assignment'
                    )
                  : getLabel(assignment);

              const structuredDueCheckpoint =
                isStructuredForm
                  ? getNextStructuredCheckpoint(
                      assignment.id,
                      formSnapshots
                    )
                  : null;

              const extensionDays =
                isStructuredForm
                  ? Math.max(
                      0,
                      Number(
                        structuredDueCheckpoint
                          ?.effective_extension_days ||
                        0
                      ) || 0
                    )
                  : (
                      assignment
                        .effective_extension_days ||
                      0
                    );

              const baseDueDate =
                isStructuredForm
                  ? (
                      structuredDueCheckpoint
                        ?.effective_base_due_date ||
                      structuredDueCheckpoint
                        ?.due_date ||
                      structuredDueCheckpoint
                        ?.close_date ||
                      null
                    )
                  : getStudentAssignmentBaseDueDate(
                      assignment
                    );

              const effectiveDueDate =
                isStructuredForm
                  ? (
                      structuredDueCheckpoint
                        ?.effective_due_date ||
                      baseDueDate
                    )
                  : getStudentAssignmentDueDate(
                      assignment
                    );

              return (
                <div
                  key={assignment.id}
                  style={styles.assignmentRow}
                >
                  <button
                    style={styles.assignmentBtn}
                    onClick={() => {
                      if (
                        selectedSequenceId &&
                        !selectedAssignmentTypeId &&
                        onOpenAssignmentType
                      ) {
                        const sameTypeAssignments =
                          filteredAssignments.filter(
                            candidate =>
                              String(
                                candidate.assignment_type_id
                              ) ===
                              String(
                                assignment.assignment_type_id
                              )
                          );

                        const typeSetting =
                          assignmentTypeSettings.find(
                            setting =>
                              String(
                                setting.assignment_type_id
                              ) ===
                              String(
                                assignment.assignment_type_id
                              )
                          );

                        const assignmentKind =
                          assignment.assignment_types
                            ?.assignment_kind ||
                          'standard';

                        const usesScheduledAssignmentPage =
                          assignmentKind !==
                            'peer_review' &&
                          assignmentKind !==
                            'structured_form' &&
                          typeSetting
                            ?.schedule_enabled ===
                            true;

                        if (
                          sameTypeAssignments.length ===
                            1 &&
                          !usesScheduledAssignmentPage
                        ) {
                          handleSelectAssignment(
                            sameTypeAssignments[0]
                          );
                          return;
                        }

                        onOpenAssignmentType(
                          assignment.assignment_type_id
                        );
                        return;
                      }

                      handleSelectAssignment(assignment);
                    }}
                  >
                    <div style={styles.assignmentLeft}>
                      <div style={styles.assignmentName}>
                        {label}
                      </div>

                      {!sequenceTypeMenu &&
                        requiresSubmission &&
                        baseDueDate && (
                          <div
                            style={
                              styles.assignmentMeta
                            }
                          >
                            Due{' '}
                            {formatDate(baseDueDate)}

                            {extensionDays > 0 && (
                              <span
                                style={
                                  styles.extensionMeta
                                }
                              >
                                {' · '}
                                Extended to{' '}
                                {formatDate(
                                  effectiveDueDate
                                )}
                              </span>
                            )}
                          </div>
                        )}

                      {!sequenceTypeMenu &&
                        !requiresSubmission &&
                        assignment.occurs_on && (
                          <div
                            style={
                              styles.assignmentMeta
                            }
                          >
                            {formatDate(
                              assignment.occurs_on
                            )}
                          </div>
                        )}
                    </div>

                    <div style={sequenceTypeMenu ? { display: 'none' } : styles.assignmentRight}>
                      {requiresSubmission && isStructuredForm ? (
                        formSnapshots.length === formInstances.length && formInstances.length ? (
                          <span style={styles.submittedPill}>
                            ✓ {formSnapshots.length}/{formInstances.length} submitted
                          </span>
                        ) : formFeedbackCount > 0 ? (
                          <span style={styles.feedbackPill}>
                            {formSnapshots.length}/{formInstances.length} submitted · {formFeedbackCount} graded
                          </span>
                        ) : (
                          <span style={formSnapshots.length ? styles.openPill : styles.notOpenPill}>
                            {formSnapshots.length}/{formInstances.length} submitted
                          </span>
                        )
                      ) : requiresSubmission ? (
                        hasFeedback ? (
                          <span
                            style={styles.feedbackPill}
                          >
                            Feedback received
                          </span>
                        ) : isSubmitted ? (
                          <span
                            style={styles.submittedPill}
                          >
                            ✓ Submitted
                          </span>
                        ) : status === 'open' ? (
                          <span
                            style={styles.openPill}
                          >
                            Open
                          </span>
                        ) : status === 'not_open' ? (
                          <span
                            style={styles.notOpenPill}
                          >
                            Not yet open
                          </span>
                        ) : status === 'past_due' ? (
                          <span
                            style={styles.openPill}
                          >
                            Past due · editable
                          </span>
                        ) : status === 'closed' ? (
                          <span
                            style={styles.closedPill}
                          >
                            Closed
                          </span>
                        ) : (
                          <span
                            style={styles.notOpenPill}
                          >
                            —
                          </span>
                        )
                      ) : (
                        <span
                          style={styles.notOpenPill}
                        >
                          View instructions
                        </span>
                      )}
                    </div>

                    <span style={styles.arrow}>
                        ›
                      </span>
                  </button>
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  aiDisclosurePanel: {
    marginTop: '18px',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '14px',
    background: '#f8fafb'
  },
  aiDisclosureHeading: {
    fontWeight: '800',
    fontSize: '15px',
    color: '#243b53',
    marginBottom: '10px'
  },
  aiQuestion: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#243b53',
    marginBottom: '8px'
  },
  radioRow: {
    display: 'flex',
    gap: '18px',
    marginBottom: '8px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#243b53',
    cursor: 'pointer'
  },
  aiYesArea: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #d9e2ec'
  },
  confirmationRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    gap: '8px',
    alignItems: 'start',
    fontSize: '13px',
    lineHeight: '1.45',
    color: '#243b53'
  },
  infoBtn: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: '1px solid #9fb5c4',
    background: 'white',
    color: '#2f6f9f',
    fontWeight: '800',
    cursor: 'pointer',
    padding: 0,
    lineHeight: '20px'
  },
  infoBox: {
    marginTop: '10px',
    border: '1px solid #c7d6e0',
    borderRadius: '5px',
    padding: '12px',
    background: 'white'
  },
  infoBoxTitle: {
    fontWeight: '700',
    fontSize: '13px',
    color: '#243b53',
    marginBottom: '8px',
    lineHeight: '1.45'
  },
  infoText: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.45',
    marginBottom: '10px'
  },
  infoSmall: {
    fontSize: '11px',
    color: '#829ab1',
    lineHeight: '1.4',
    marginTop: '10px'
  },
  noAiAllowed: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '4px',
    padding: '9px 10px',
    color: '#a12a2a',
    fontSize: '12px',
    lineHeight: '1.4',
    marginBottom: '10px'
  },
  permissionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  permissionItem: {
    borderTop: '1px solid #eef2f5',
    paddingTop: '8px'
  },
  permissionTitle: {
    fontWeight: '800',
    fontSize: '13px',
    color: '#2f6f9f'
  },
  permissionDescription: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.45',
    marginTop: '3px'
  },
  permissionNote: {
    fontSize: '12px',
    color: '#627d98',
    lineHeight: '1.4',
    marginTop: '4px',
    fontStyle: 'italic'
  },
  urlRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '8px',
    alignItems: 'center',
    marginTop: '12px'
  },
  urlInput: {
    width: '100%',
    border: '1px solid #bcccdc',
    borderRadius: '4px',
    padding: '9px 10px',
    boxSizing: 'border-box',
    fontSize: '13px',
    background: 'white'
  },
  providerGuideList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  providerGuide: {
    border: '1px solid #e1e7eb',
    borderRadius: '4px',
    padding: '8px'
  },
  providerName: {
    fontWeight: '800',
    fontSize: '12px',
    color: '#243b53',
    marginBottom: '5px'
  },
  providerMockWindow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '5px',
    fontSize: '11px',
    color: '#627d98',
    background: '#f7f8fa',
    borderRadius: '5px',
    padding: '7px'
  },
  providerArrow: {
    color: '#9aa8b3'
  },
  providerShareButton: {
    border: '1px solid #9fb5c4',
    borderRadius: '5px',
    padding: '2px 6px',
    color: '#2f6f9f',
    background: 'white',
    fontWeight: '700'
  },
  aiDisclosureReadOnly: {
    marginTop: '12px',
    border: '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '12px',
    background: '#f8fafb',
    fontSize: '13px',
    color: '#243b53',
    lineHeight: '1.5'
  },
  aiDisclosureLinkRow: {
    marginTop: '5px',
    overflowWrap: 'anywhere'
  },
  selectedFile: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#627d98',
    overflowWrap: 'anywhere'
  },
  submitFileBtn: {
    marginTop: '12px',
    background: '#2f6f9f',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  wrap: {
    minHeight: 0,
    background: 'transparent'
  },

  content: { padding: 0, maxWidth: 'none', margin: 0 },

  muted: {
    color: '#627d98',
    fontSize: '14px'
  },

  heading: {
    margin: '0 0 20px',
    fontSize: '26px',
    color: '#243b53',
    fontWeight: '700'
  },

  backLink: {
    border: 'none',
    background: 'none',
    color: '#2f6f9f',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0 0 16px',
    display: 'block'
  },

  descriptionBox: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '14px'
  },

  descriptionToggle: {
    border: 'none',
    background: 'none',
    color: '#2f6f9f',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0 0 8px'
  },

  descriptionContent: {
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#243b53'
  },

  dueBanner: {
    background: '#fff4d7',
    border: '1px solid #ead494',
    borderRadius: '5px',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#8a5b00',
    marginBottom: '16px'
  },

  extensionNotice: {
    marginTop: '5px',
    color: '#6c4b00'
  },

  feedbackBox: {
    background: '#eef5fa',
    border: '1px solid #c5d6e3',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px'
  },

  feedbackLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#627d98',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },

  feedbackText: {
    fontSize: '15px',
    color: '#243b53',
    lineHeight: '1.6'
  },

  completionStatus: {
    fontSize: '13px',
    color: '#2f6f9f',
    marginTop: '8px',
    fontWeight: '600'
  },

  responseForm: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px'
  },

  responseLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '700',
    color: '#243b53'
  },

  responseHint: {
    fontSize: '11px',
    color: '#82909c',
    margin: '2px 0 8px'
  },

  responseBtn: {
    marginTop: '12px',
    background: 'transparent',
    color: '#2f6f9f',
    border: '1px solid #c5d6e3',
    borderRadius: '5px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  responseBox: {
    background: 'white',
    border: '1px solid #c5d6e3',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px'
  },

  uploadBox: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '20px'
  },

  submittedInfo: {
    marginBottom: '14px'
  },

  submittedBanner: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '5px',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#216e45',
    marginBottom: '8px'
  },

  fileName: {
    fontSize: '14px',
    color: '#243b53',
    marginBottom: '6px'
  },

  resubmitNote: {
    fontSize: '13px',
    color: '#627d98'
  },

  notSubmitted: {
    fontSize: '14px',
    color: '#627d98',
    marginBottom: '14px'
  },

  uploadLabel: {
    cursor: 'pointer',
    display: 'inline-block'
  },

  uploadBtn: {
    background: '#2f6f9f',
    color: 'white',
    borderRadius: '5px',
    padding: '11px 20px',
    fontSize: '15px',
    fontWeight: '600',
    display: 'inline-block',
    cursor: 'pointer'
  },

  closedBanner: {
    background: '#edf1f5',
    border: '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#627d98',
    marginTop: '8px'
  },

  assignmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  assignmentRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },

  assignmentBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left'
  },

  assignmentLeft: {
    flex: 1
  },

  assignmentName: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#243b53'
  },

  assignmentMeta: {
    fontSize: '12px',
    color: '#627d98',
    marginTop: '2px'
  },

  extensionMeta: {
    color: '#8a5b00',
    fontWeight: '600'
  },

  assignmentRight: {
    flexShrink: 0
  },

  arrow: {
    color: '#627d98',
    fontSize: '20px',
    flexShrink: 0
  },

  submittedPill: {
    background: '#e7f5ed',
    color: '#216e45',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  feedbackPill: {
    background: '#eef5fa',
    color: '#2f6f9f',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  openPill: {
    background: '#eef5fa',
    color: '#2f6f9f',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  notOpenPill: {
    background: '#edf1f5',
    color: '#627d98',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  closedPill: {
    background: '#faeaea',
    color: '#a12a2a',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '5px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '5px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },

  lockedNote: {
    fontSize: '13px',
    color: '#627d98',
    marginTop: '8px',
    fontStyle: 'italic'
  }
};
